import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import FeatherIcon from "feather-icons-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const IconDropdown = ({ options, activeId, onChange, right=true, className }) => {
    const [open, setOpen] = useState(false);
    const [menuStyle, setMenuStyle] = useState({});
    const { t } = useTranslation();
    const triggerRef = useRef();
    const menuRef = useRef();

    useEffect(() => {
        // menuRef is checked too - the menu itself is portaled straight into
        // document.body (see render below), so it's no longer a DOM
        // descendant of triggerRef and a click inside it would otherwise
        // look like an "outside" click and close the menu before the
        // option's own onClick ever fires.
        let handler = (e) => {
            if (
                triggerRef.current && !triggerRef.current.contains(e.target) &&
                menuRef.current && !menuRef.current.contains(e.target)
            ) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => {
            document.removeEventListener("mousedown", handler);
        };
    }, []);

    const activeOption = options.find(opt => opt.id === activeId) || options[0];

    const toggleOpen = (e) => {
        e.preventDefault();
        if (!open) {
            // .sticky-toolbar (the usual ancestor here) sets its own
            // position:sticky + z-index, which creates a stacking context -
            // a z-index on a descendant, portaled or not, only ranks against
            // siblings inside that same context, so it could never actually
            // paint above content outside the toolbar no matter how high.
            // Portaling straight into document.body sidesteps that (and the
            // toolbar's clip-path: inset(...) full-width-background trick,
            // which was clipping the menu at the toolbar's own edges)
            // entirely - the menu is a sibling of the toolbar in the DOM,
            // not a descendant, positioned via getBoundingClientRect().
            const rect = e.currentTarget.getBoundingClientRect();
            setMenuStyle({
                position: "fixed",
                top: `${rect.bottom + 4}px`,
                right: `${document.documentElement.clientWidth - rect.right}px`,
                // .options-menu's own CSS sets min-width:100% - relative to
                // this tiny icon-only trigger when position was absolute,
                // that was harmless, but 100% resolves against the viewport
                // once fixed, ballooning the menu to full screen width.
                minWidth: "140px",
                width: "max-content",
                zIndex: 9999
            });
        }
        setOpen(!open);
    };

    const menu = (
        <div
            ref={menuRef}
            className={`options-menu ${open ? "block_active" : "block_inactive"}`}
            style={{
                ...menuStyle,
                border: 0,
                cursor: "pointer",
                // .block_active/.block_inactive's own transition has no
                // property listed (defaults to "all"), which was animating
                // top/right along with opacity - since the menu sits at the
                // base CSS's default top:45px/right:0 (top-right corner of
                // the page, now that it's portaled straight into <body>
                // instead of the tiny trigger <li>) until the first click
                // computes its real position, that made it visibly slide in
                // from the corner every time it opened. Same fix already
                // used for TournamentMenuList's dots-menu.
                transitionProperty: "opacity, transform, visibility"
            }}
        >
            <div className="options-content-block" style={{ border: "none", zIndex: 0 }}>
                {options.map((option) => {
                    const content = (
                        <>
                            <FeatherIcon icon={option.icon} size={16} style={{ marginRight: "10px", marginBottom: "-2px" }} />
                            {t(option.label)}
                        </>
                    );

                    if (option.to) {
                        return (
                            <Link
                                key={option.id}
                                to={option.to}
                                className={`option ${option.id === activeId ? "active" : ""}`}
                                onClick={() => setOpen(false)}
                                style={{ display: "flex", alignItems: "center", textDecoration: "none" }}
                            >
                                {content}
                            </Link>
                        );
                    }

                    return (
                        <div
                            key={option.id}
                            className={`option ${option.id === activeId ? "active" : ""}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                setOpen(false);
                                if (onChange) onChange(option.id);
                            }}
                            style={{ display: "flex", alignItems: "center" }}
                        >
                            {content}
                        </div>
                    );
                })}
            </div>
        </div>
    );

    return (
        <li className={`${right ? "right " : ""}icon-dropdown toggle-grid active`} ref={triggerRef} style={{ position: "relative" }}>
            <a href="#" className={`active${className ? ` ${className}` : ""}`} onClick={toggleOpen}>
                <FeatherIcon icon={activeOption?.icon || "menu"} />
            </a>
            {ReactDOM.createPortal(menu, document.body)}
        </li>
    );
};

export default IconDropdown;
